import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Receipt, 
  AlertTriangle,
  Clock,
  CheckCircle,
  Building2,
  CreditCard,
  User
} from 'lucide-react';
import { toast } from 'sonner';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

const ReceivablesPage = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchReceivables();
  }, []);

  const fetchReceivables = async () => {
    try {
      const response = await apiClient.get('/receivables');
      setData(response.data);
    } catch (error) {
      toast.error('Failed to load receivables');
    } finally {
      setLoading(false);
    }
  };

  const updateClaimStatus = async (recordId, newStatus) => {
    try {
      await apiClient.put(`/financial-records/${recordId}/update-status`, null, {
        params: { claim_status: newStatus }
      });
      toast.success('Status updated');
      fetchReceivables();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const updatePaymentStatus = async (recordId, newStatus, amountPaid = null) => {
    try {
      const params = { payment_status: newStatus };
      if (amountPaid !== null) {
        params.amount_paid = amountPaid;
      }
      await apiClient.put(`/financial-records/${recordId}/update-status`, null, { params });
      toast.success('Payment status updated');
      fetchReceivables();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-PH', {
      style: 'currency',
      currency: 'PHP',
      minimumFractionDigits: 0,
    }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-PH', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  const totalOutstanding = (data?.hmo_philhealth?.total_outstanding || 0) + (data?.credit?.total_outstanding || 0);

  return (
    <div className="space-y-6" data-testid="receivables-page">
      {/* Header */}
      <div>
        <h1 className="font-heading text-2xl font-bold flex items-center gap-3">
          <Receipt className="w-6 h-6 text-primary" strokeWidth={1.5} />
          Receivables
        </h1>
        <p className="text-muted-foreground text-sm mt-1">
          Track outstanding HMO/PhilHealth claims and patient credits
        </p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-primary">{formatCurrency(totalOutstanding)}</p>
            <p className="text-xs text-muted-foreground mt-1">Total Outstanding</p>
          </CardContent>
        </Card>
        <Card className="border-amber-200 bg-amber-50/50">
          <CardContent className="p-4 text-center">
            <Building2 className="w-5 h-5 text-amber-600 mx-auto mb-1" />
            <p className="text-2xl font-bold text-amber-600">{formatCurrency(data?.hmo_philhealth?.total_outstanding)}</p>
            <p className="text-xs text-muted-foreground">HMO/PhilHealth</p>
          </CardContent>
        </Card>
        <Card className="border-blue-200 bg-blue-50/50">
          <CardContent className="p-4 text-center">
            <CreditCard className="w-5 h-5 text-blue-600 mx-auto mb-1" />
            <p className="text-2xl font-bold text-blue-600">{formatCurrency(data?.credit?.total_outstanding)}</p>
            <p className="text-xs text-muted-foreground">Patient Credits</p>
          </CardContent>
        </Card>
        <Card className="border-red-200 bg-red-50/50">
          <CardContent className="p-4 text-center">
            <AlertTriangle className="w-5 h-5 text-red-600 mx-auto mb-1" />
            <p className="text-2xl font-bold text-red-600">{data?.credit?.overdue_count || 0}</p>
            <p className="text-xs text-muted-foreground">Overdue</p>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="hmo">
        <TabsList>
          <TabsTrigger value="hmo">
            HMO/PhilHealth ({data?.hmo_philhealth?.count || 0})
          </TabsTrigger>
          <TabsTrigger value="credit">
            Patient Credits ({data?.credit?.count || 0})
          </TabsTrigger>
          <TabsTrigger value="overdue" className="text-destructive">
            Overdue ({data?.credit?.overdue_count || 0})
          </TabsTrigger>
        </TabsList>

        {/* HMO/PhilHealth Tab */}
        <TabsContent value="hmo" className="space-y-3 mt-4">
          {(data?.hmo_philhealth?.records?.length || 0) === 0 ? (
            <Card>
              <CardContent className="py-8 text-center">
                <Building2 className="w-12 h-12 text-muted-foreground/30 mx-auto mb-4" />
                <p className="text-muted-foreground">No pending HMO/PhilHealth claims</p>
              </CardContent>
            </Card>
          ) : (
            data.hmo_philhealth.records.map((record, index) => (
              <Card 
                key={record.record_id}
                className="animate-fade-in"
                style={{ animationDelay: `${index * 0.05}s` }}
              >
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-medium">{record.patient_name || 'No patient'}</span>
                        <Badge variant="outline" className="capitalize">
                          {record.payment_type}
                        </Badge>
                        <Badge 
                          className={
                            record.claim_status === 'pending' ? 'bg-amber-100 text-amber-800' :
                            record.claim_status === 'submitted' ? 'bg-blue-100 text-blue-800' :
                            'bg-gray-100 text-gray-800'
                          }
                        >
                          {record.claim_status}
                        </Badge>
                      </div>
                      <div className="text-sm text-muted-foreground">
                        {record.provider_name && <span>{record.provider_name} • </span>}
                        {record.approval_code && <span>Code: {record.approval_code} • </span>}
                        <span>{formatDate(record.created_at)}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <p className="text-lg font-bold">{formatCurrency(record.amount)}</p>
                      <Select 
                        value={record.claim_status}
                        onValueChange={(v) => updateClaimStatus(record.record_id, v)}
                      >
                        <SelectTrigger className="w-32">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="pending">Pending</SelectItem>
                          <SelectItem value="submitted">Submitted</SelectItem>
                          <SelectItem value="paid">Paid</SelectItem>
                          <SelectItem value="rejected">Rejected</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </TabsContent>

        {/* Patient Credits Tab */}
        <TabsContent value="credit" className="space-y-3 mt-4">
          {(data?.credit?.records?.length || 0) === 0 ? (
            <Card>
              <CardContent className="py-8 text-center">
                <CreditCard className="w-12 h-12 text-muted-foreground/30 mx-auto mb-4" />
                <p className="text-muted-foreground">No outstanding patient credits</p>
              </CardContent>
            </Card>
          ) : (
            data.credit.records.map((record, index) => (
              <Card 
                key={record.record_id}
                className={`animate-fade-in ${record.due_date && new Date(record.due_date) < new Date() ? 'border-l-4 border-l-red-500' : ''}`}
                style={{ animationDelay: `${index * 0.05}s` }}
              >
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <User className="w-4 h-4 text-muted-foreground" />
                        <span className="font-medium">{record.patient_name || 'No patient'}</span>
                        <Badge variant="outline" className="capitalize">
                          {record.payment_status}
                        </Badge>
                      </div>
                      <div className="text-sm text-muted-foreground">
                        <span>Total: {formatCurrency(record.amount)}</span>
                        {record.amount_paid > 0 && <span> • Paid: {formatCurrency(record.amount_paid)}</span>}
                        {record.due_date && (
                          <span className={new Date(record.due_date) < new Date() ? 'text-red-600 font-medium' : ''}>
                            {' '}• Due: {formatDate(record.due_date)}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="text-right">
                        <p className="text-lg font-bold text-blue-600">{formatCurrency(record.balance)}</p>
                        <p className="text-xs text-muted-foreground">Balance</p>
                      </div>
                      <Button
                        size="sm"
                        onClick={() => updatePaymentStatus(record.record_id, 'paid', record.amount)}
                        data-testid={`mark-paid-${record.record_id}`}
                      >
                        <CheckCircle className="w-4 h-4 mr-1" />
                        Paid
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </TabsContent>

        {/* Overdue Tab */}
        <TabsContent value="overdue" className="space-y-3 mt-4">
          {(data?.credit?.overdue_records?.length || 0) === 0 ? (
            <Card>
              <CardContent className="py-8 text-center">
                <CheckCircle className="w-12 h-12 text-green-500/30 mx-auto mb-4" />
                <p className="text-muted-foreground">No overdue receivables</p>
              </CardContent>
            </Card>
          ) : (
            data.credit.overdue_records.map((record, index) => (
              <Card 
                key={record.record_id}
                className="animate-fade-in border-l-4 border-l-red-500"
                style={{ animationDelay: `${index * 0.05}s` }}
              >
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <AlertTriangle className="w-4 h-4 text-red-600" />
                        <span className="font-medium">{record.patient_name || 'No patient'}</span>
                        <Badge variant="destructive">
                          {record.days_overdue} days overdue
                        </Badge>
                      </div>
                      <div className="text-sm text-muted-foreground">
                        <span>Total: {formatCurrency(record.amount)}</span>
                        <span> • Due: {formatDate(record.due_date)}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="text-right">
                        <p className="text-lg font-bold text-red-600">{formatCurrency(record.balance)}</p>
                        <p className="text-xs text-muted-foreground">Overdue</p>
                      </div>
                      <Button
                        size="sm"
                        onClick={() => updatePaymentStatus(record.record_id, 'paid', record.amount)}
                      >
                        <CheckCircle className="w-4 h-4 mr-1" />
                        Mark Paid
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </TabsContent>
      </Tabs>

      {/* Info Card */}
      <Card className="bg-muted/50">
        <CardContent className="p-4">
          <p className="text-sm text-muted-foreground">
            <strong>Note:</strong> Only paid amounts are counted as income. HMO/PhilHealth claims and patient credits 
            remain as receivables until marked as paid.
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default ReceivablesPage;
